import React, { useMemo } from 'react';
import { PackageTrackingDetails } from '../../../types';
import { useTranslation } from 'react-i18next';
import AddressComponent from '../Shared/AddressComponent'
import LabeledComponent from '../Shared/LabeledComponent'
import styled from 'styled-components';

const ShipmentDetailGroup = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    padding-bottom: 10px;
`

const AddressDetailWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
`;

const AddressDetail = ({address, name}) => {
    return (
        <LabeledComponent labelText={name}>
            <AddressComponent address={address} />
        </LabeledComponent>
    );
}

const SignatureWrapper = styled.div`
    height: 100%;
`

const SignatureDetail = ({signature, labelText}) => {
    return (
        <>
            <LabeledComponent labelText={labelText}>
                <SignatureWrapper>
                    <div>{signature.Name}</div>
                    <img className='signature' src={'data:image/png;base64,' + signature.Base64EncodedImage} alt='signature' />
                </SignatureWrapper>
            </LabeledComponent>
        </>
    );
}

const ShipmentPropertyWrapper = styled.div`
    max-width: 250px;
`

const ShipmentPropertyContent = styled.div`
    word-wrap: break-word;
`

const ShipmentProperty = ({children, name}) => {
    return (
        <ShipmentPropertyWrapper>
            <LabeledComponent labelText={name}>
                <ShipmentPropertyContent>
                    {children}
                </ShipmentPropertyContent>
            </LabeledComponent>
        </ShipmentPropertyWrapper>
        )
}

interface ShipmentDetailsProps {
    packageDetails: PackageTrackingDetails
};

const ShipmentDetailsComponent = ({ packageDetails }: ShipmentDetailsProps) => {
    const [t] = useTranslation();

    return (
        <div id={'shipment_details'} className={'pieces'}>
            {/* Details */}
            {(packageDetails.MerchantName || packageDetails.CustomerComments || packageDetails.LastCallingCardAction?.CallingCard ||
                packageDetails.LastSafePlaceAction?.SafePlace || Object.keys(packageDetails.JobCustomDataDict).length > 0) &&
                <ShipmentDetailGroup className={'smlpiece'}>
                    {/* Merchant */}
                    {packageDetails.MerchantName &&
                        <ShipmentProperty name={t('package_tracking_details_merchant')}>
                            {packageDetails.MerchantName}
                        </ShipmentProperty>}

                    {/* Custom Data */}
                    {Object.keys(packageDetails.JobCustomDataDict).map((key) => {
                        return(
                            packageDetails.JobCustomDataDict[key] ?
                                <ShipmentProperty name={key} key={key}>
                                    {packageDetails.JobCustomDataDict[key]}
                                </ShipmentProperty> 
                                : null
                        );
                    })}

                    {/* Customer Comments */}
                    {packageDetails.CustomerComments &&
                        <ShipmentProperty name={t('package_tracking_details_customer_comments')}>
                            {packageDetails.CustomerComments}
                        </ShipmentProperty>}
                
                    {/* Calling Card Action */}
                    {packageDetails.LastCallingCardAction?.CallingCard &&
                        <>
                            <ShipmentProperty name={t('history_model_calling_card')}>
                                {packageDetails.LastCallingCardAction.CallingCard}
                            </ShipmentProperty>
                            {packageDetails.LastCallingCardAction.CallingCardLocation &&
                                <ShipmentProperty name={t('history_model_calling_card_location')}>
                                    {packageDetails.LastCallingCardAction.CallingCardLocation}
                                </ShipmentProperty>}
                        </>}
                    
                    {/* Safe Place Action */}
                    {packageDetails.LastSafePlaceAction?.SafePlace &&
                        <>
                            <ShipmentProperty name={t('history_model_safe_place')}>
                                {packageDetails.LastSafePlaceAction.SafePlace}
                            </ShipmentProperty>
                            {packageDetails.LastSafePlaceAction.SafePlaceDetails &&
                                <ShipmentProperty name={t('history_model_safe_place_details')}>
                                    {packageDetails.LastSafePlaceAction.SafePlaceDetails}
                                </ShipmentProperty>}
                        </>}
                </ShipmentDetailGroup>}


            {/* Collection Address and Signature */}
            {packageDetails.CollectionAddress &&
                <ShipmentDetailGroup id='fromaddress' className={'smlpiece'}>
                    <AddressDetailWrapper>
                        <AddressDetail address={packageDetails.CollectionAddress} name={t('package_tracking_details_from')} />
                        {packageDetails.CollectionSignature &&
                            <SignatureDetail signature={packageDetails.CollectionSignature} labelText={t('package_tracking_details_collection_signature')} />}
                    </AddressDetailWrapper>
                </ShipmentDetailGroup>}

            {/* Delivery Address and Signature */}
            {packageDetails.DeliveryAddress &&
                <ShipmentDetailGroup id='toaddress' className={'smlpiece'}>
                    <AddressDetailWrapper>
                        <AddressDetail address={packageDetails.DeliveryAddress} name={t('package_tracking_details_to')} />
                        {packageDetails.DeliverySignature &&
                            <SignatureDetail signature={packageDetails.DeliverySignature} labelText={t('package_tracking_details_delivery_signature')} />}
                    </AddressDetailWrapper>
                </ShipmentDetailGroup>}
        </div>
    );
};

export default ShipmentDetailsComponent;